/**
 * Settings Panel
 * 
 * Configuration panel for game settings, including travel time.
 */

import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { AxialCoord } from '../types/terrain';
import { FEATURES } from '../config/features';

const STORAGE_KEY = 'storyworld_travel_time_ms';
const MONSTER_PROBABILITY_KEY = 'storyworld_monster_probability';
const FORAGING_TIME_KEY = 'storyworld_foraging_time_ms';
const ALWAYS_NYMPH_KEY = 'storyworld_always_nymph';
const TEST_QUEST_LENGTH_KEY = 'storyworld_test_quest_length';

interface SettingsPanelProps {
  onClose: () => void;
  islandCenter?: AxialCoord;
  onResetPosition?: () => void;
  onFullHPClick?: () => void;
  onFullStaminaClick?: () => void;
  onAIToggleClick?: () => void;
  aiStoriesEnabled?: boolean;
  onLeaveGameClick?: () => void;
  isInCombat?: boolean;
  onRegenerateMapClick?: () => void;
}

const TRAVEL_TIME_OPTIONS = [
  { label: 'Instant (Testing)', value: 0 },
  { label: '5 seconds', value: 5 * 1000 },
  { label: '1 minute', value: 60 * 1000 },
  { label: '5 minutes', value: 5 * 60 * 1000 },
  { label: '30 minutes (Realistic)', value: 30 * 60 * 1000 },
];

const FORAGING_TIME_OPTIONS = [
  { label: 'Instant (Testing)', value: 0 },
  { label: '10 seconds', value: 10 * 1000 },
  { label: '30 seconds', value: 30 * 1000 },
  { label: '1 minute', value: 60 * 1000 },
  { label: '5 minutes', value: 5 * 60 * 1000 },
  { label: '1 hour (Realistic)', value: 60 * 60 * 1000 },
];

const MONSTER_PROBABILITY_OPTIONS = [
  { label: 'No Monsters (0%)', value: 0 },
  { label: 'Default (1/12 ~ 8%)', value: 1/12 },
  { label: 'Very Rare (10%)', value: 0.1 },
  { label: 'Rare (20%)', value: 0.2 },
  { label: 'Common (50%)', value: 0.5 },
  { label: 'Very Common (80%)', value: 0.8 },
  { label: 'Always (100%)', value: 1.0 },
];

export function SettingsPanel({ 
  onClose, 
  islandCenter, 
  onResetPosition,
  onFullHPClick,
  onFullStaminaClick,
  onAIToggleClick,
  aiStoriesEnabled = true,
  onLeaveGameClick,
  isInCombat = false,
  onRegenerateMapClick,
}: SettingsPanelProps) {
  const [travelTime, setTravelTime] = useState<number>(0);
  const [monsterProbability, setMonsterProbability] = useState<number>(0);
  const [foragingTime, setForagingTime] = useState<number>(60 * 60 * 1000); // Default 1 hour
  const [alwaysNymph, setAlwaysNymph] = useState<boolean>(false);
  const [testQuestLength, setTestQuestLength] = useState<boolean>(false);

  // Load settings from localStorage on mount
  useEffect(() => {
    const savedTravelTime = localStorage.getItem(STORAGE_KEY);
    if (savedTravelTime) {
      setTravelTime(parseInt(savedTravelTime, 10));
    }
    
    const savedMonsterProb = localStorage.getItem(MONSTER_PROBABILITY_KEY);
    if (savedMonsterProb) {
      setMonsterProbability(parseFloat(savedMonsterProb));
    }
    
    const savedForagingTime = localStorage.getItem(FORAGING_TIME_KEY);
    if (savedForagingTime) {
      setForagingTime(parseInt(savedForagingTime, 10));
    }
    
    const savedAlwaysNymph = localStorage.getItem(ALWAYS_NYMPH_KEY);
    if (savedAlwaysNymph) {
      setAlwaysNymph(savedAlwaysNymph === 'true');
    }
    
    const savedTestQuestLength = localStorage.getItem(TEST_QUEST_LENGTH_KEY);
    if (savedTestQuestLength) {
      setTestQuestLength(savedTestQuestLength === 'true');
    }
  }, []);

  // Save travel time to localStorage when changed
  const handleTravelTimeChange = (value: number) => {
    setTravelTime(value);
    localStorage.setItem(STORAGE_KEY, value.toString());
  };

  // Save monster probability to localStorage when changed
  const handleMonsterProbabilityChange = (value: number) => {
    setMonsterProbability(value);
    localStorage.setItem(MONSTER_PROBABILITY_KEY, value.toString());
    console.log(`🎲 Monster Encounter Probability set to: ${(value * 100).toFixed(0)}%`);
  };

  // Save foraging time to localStorage when changed
  const handleForagingTimeChange = (value: number) => {
    setForagingTime(value);
    localStorage.setItem(FORAGING_TIME_KEY, value.toString());
    console.log(`🌾 Foraging/Resting Duration set to: ${value === 0 ? 'Instant' : `${value / 1000}s`}`);
  };

  // Save always nymph setting to localStorage when changed
  const handleAlwaysNymphChange = (value: boolean) => {
    setAlwaysNymph(value);
    localStorage.setItem(ALWAYS_NYMPH_KEY, value.toString());
    console.log(`🧜 Always Nymph Mode: ${value ? 'Enabled' : 'Disabled'}`);
  };

  // Save test quest length setting to localStorage when changed
  const handleTestQuestLengthChange = (value: boolean) => {
    setTestQuestLength(value);
    localStorage.setItem(TEST_QUEST_LENGTH_KEY, value.toString());
    console.log(`🧪 Test Quest Length: ${value ? 'Enabled (2 sections, 2 quests)' : 'Disabled (12 sections, 5 quests)'}`);
  };

  return ReactDOM.createPortal(
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
      }}
      onClick={onClose}
      data-no-sync="true"
    >
      <div
        style={{
          backgroundColor: '#2a2a2a',
          color: '#e0e0e0',
          padding: '24px',
          borderRadius: '8px',
          minWidth: '400px',
          maxWidth: '90%',
          maxHeight: '90vh',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
        data-no-sync="true"
      >
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '20px',
          flexShrink: 0,
        }}>
          <h2 style={{ margin: 0, fontSize: '24px' }}>Settings</h2>
          <button
            onClick={onClose}
            data-no-sync="true"
            style={{
              background: 'transparent',
              border: 'none',
              color: '#e0e0e0',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '0',
              width: '30px',
              height: '30px',
            }}
          >
            ×
          </button>
        </div>

        <div style={{ 
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          paddingRight: '8px',
        }}>
          {FEATURES.SHOW_TIMER_SETTINGS && (
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ 
                marginTop: 0, 
                marginBottom: '12px',
                fontSize: '16px',
                color: '#b0b0b0',
              }}>
                Travel Time (Exploration Mode)
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {TRAVEL_TIME_OPTIONS.map((option) => (
                  <label
                    key={option.value}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      padding: '8px',
                      borderRadius: '4px',
                      backgroundColor: travelTime === option.value ? '#3a3a3a' : 'transparent',
                      transition: 'background-color 0.2s',
                    }}
                  >
                    <input
                      type="radio"
                      name="travelTime"
                      value={option.value}
                      checked={travelTime === option.value}
                      onChange={(e) => handleTravelTimeChange(parseInt(e.target.value, 10))}
                      style={{ marginRight: '12px', cursor: 'pointer' }}
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
              <p style={{ 
                fontSize: '12px', 
                color: '#888', 
                marginTop: '12px',
                marginBottom: 0,
              }}>
                This setting controls how long it takes to travel between hexes in Exploration Mode.
                Quest Mode is always instant.
              </p>
            </div>
          )}

        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ 
            marginTop: 0, 
            marginBottom: '12px',
            fontSize: '16px',
            color: '#b0b0b0',
          }}>
            Monster Encounter Probability
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {MONSTER_PROBABILITY_OPTIONS.map((option) => (
              <label
                key={option.value}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '4px',
                  backgroundColor: monsterProbability === option.value ? '#3a3a3a' : 'transparent',
                  transition: 'background-color 0.2s',
                }}
              >
                <input
                  type="radio"
                  name="monsterProbability"
                  value={option.value}
                  checked={monsterProbability === option.value}
                  onChange={(e) => handleMonsterProbabilityChange(parseFloat(e.target.value))}
                  style={{ marginRight: '12px', cursor: 'pointer' }}
                />
                <span>{option.label}</span>
              </label>
            ))}
          </div>
          <p style={{ 
            fontSize: '12px', 
            color: '#888', 
            marginTop: '12px',
            marginBottom: 0,
          }}>
            Controls the chance of encountering a random monster when entering a hex in Exploration Mode.
            Settlements never have monster encounters.
          </p>
        </div>

        {FEATURES.SHOW_TIMER_SETTINGS && (
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ 
              marginTop: 0, 
              marginBottom: '12px',
              fontSize: '16px',
              color: '#b0b0b0',
            }}>
              Foraging/Resting Duration
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {FORAGING_TIME_OPTIONS.map((option) => (
                <label
                  key={option.value}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    padding: '8px',
                    borderRadius: '4px',
                    backgroundColor: foragingTime === option.value ? '#3a3a3a' : 'transparent',
                    transition: 'background-color 0.2s',
                  }}
                >
                  <input
                    type="radio"
                    name="foragingTime"
                    value={option.value}
                    checked={foragingTime === option.value}
                    onChange={(e) => handleForagingTimeChange(parseInt(e.target.value, 10))}
                    style={{ marginRight: '12px', cursor: 'pointer' }}
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
            <p style={{ 
              fontSize: '12px', 
              color: '#888', 
              marginTop: '12px',
              marginBottom: 0,
            }}>
              Controls how long it takes to forage for rations. Rations gained depend on terrain type (Desert/Settlement: 0, Mountain: 1, Grassland/Hills: 2, Forest/River: 3).
            </p>
          </div>
        )}

        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ 
            marginTop: 0, 
            marginBottom: '12px',
            fontSize: '16px',
            color: '#b0b0b0',
          }}>
            Always Nymph Mode
          </h3>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '4px',
              backgroundColor: alwaysNymph ? '#3a3a3a' : 'transparent',
              transition: 'background-color 0.2s',
            }}
          >
            <input
              type="checkbox"
              checked={alwaysNymph}
              onChange={(e) => handleAlwaysNymphChange(e.target.checked)}
              style={{ marginRight: '12px', cursor: 'pointer' }}
            />
            <span>All monsters are Nymphs 🧜</span>
          </label>
          <p style={{ 
            fontSize: '12px', 
            color: '#888', 
            marginTop: '12px',
            marginBottom: 0,
          }}>
            When enabled, every monster encounter will be a Nymph (useful for testing or an easier gameplay experience).
          </p>
        </div>

        {/* Test Quest Length */}
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ 
            marginBottom: '12px', 
            fontSize: '14px',
            borderBottom: '1px solid #444',
            paddingBottom: '8px'
          }}>
            Test Quest Length 🧪
          </h3>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '4px',
              backgroundColor: testQuestLength ? '#3a3a3a' : 'transparent',
              transition: 'background-color 0.2s',
            }}
          >
            <input
              type="checkbox"
              checked={testQuestLength}
              onChange={(e) => handleTestQuestLengthChange(e.target.checked)}
              style={{ marginRight: '12px', cursor: 'pointer' }}
            />
            <span>Short quests (2 sections, 2 quests)</span>
          </label>
          <p style={{ 
            fontSize: '12px', 
            color: '#888', 
            marginTop: '12px',
            marginBottom: 0,
          }}>
            When enabled, campaigns have 2 quests (instead of 5) and each quest has 2 sections (instead of 12). Useful for quickly testing quest/campaign completion flows.
          </p>
        </div>

        {/* Game Controls Section */}
        <div style={{ marginBottom: '20px', paddingTop: '20px', borderTop: '1px solid #3a3a3a' }}>
          <h3 style={{ 
            marginTop: 0, 
            marginBottom: '12px',
            fontSize: '16px',
            color: '#b0b0b0',
          }}>
            Game Controls
          </h3>
          
          {onFullHPClick && (
            <button
              onClick={() => {
                onFullHPClick();
              }}
              style={{
                width: '100%',
                padding: '12px',
                marginBottom: '12px',
                backgroundColor: '#8b4513',
                color: '#ffffff',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
              }}
              data-no-sync="true"
            >
              ❤️ Full HP
            </button>
          )}

          {onFullStaminaClick && (
            <button
              onClick={() => {
                onFullStaminaClick();
              }}
              data-no-sync="true"
              style={{
                width: '100%',
                padding: '12px',
                marginBottom: '12px',
                backgroundColor: '#8b4513',
                color: '#ffffff',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
              }}
            >
              ⚡ Full Stamina
            </button>
          )}

          {onAIToggleClick && (
            <div style={{ marginBottom: '12px' }}>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  padding: '12px',
                  borderRadius: '4px',
                  backgroundColor: aiStoriesEnabled ? '#3a3a3a' : 'transparent',
                  border: '1px solid #666',
                  transition: 'background-color 0.2s',
                }}
                data-no-sync="true"
              >
                <input
                  type="checkbox"
                  checked={aiStoriesEnabled}
                  onChange={() => onAIToggleClick()}
                  style={{ marginRight: '12px', cursor: 'pointer' }}
                  data-no-sync="true"
                />
                <span style={{ fontWeight: 600 }}>
                  {aiStoriesEnabled ? '🤖 AI Stories: ON' : '🔇 AI Stories: OFF'}
                </span>
              </label>
              <p style={{ 
                fontSize: '12px', 
                color: '#888', 
                marginTop: '8px',
                marginBottom: 0,
              }}>
                Toggle AI-generated stories to save API costs during development.
              </p>
            </div>
          )}

          {onLeaveGameClick && (
            <button
              onClick={() => {
                if (isInCombat) {
                  const confirmed = window.confirm(
                    'You are currently in combat! Leaving now will resolve the combat automatically. Are you sure you want to leave?'
                  );
                  if (!confirmed) return;
                }
                onLeaveGameClick();
                onClose();
              }}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: isInCombat ? '#666' : '#4169e1',
                color: '#ffffff',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
              }}
              data-no-sync="true"
            >
              🚪 Leave Game
            </button>
          )}
        </div>

          {/* Reset Position Button */}
          {islandCenter && onResetPosition && (
            <div style={{ marginBottom: '20px', paddingTop: '20px', borderTop: '1px solid #3a3a3a' }}>
              <h3 style={{ 
                marginTop: 0, 
                marginBottom: '12px',
                fontSize: '16px',
                color: '#b0b0b0',
              }}>
                Debug Tools
              </h3>
              <button
                onClick={() => {
                  onResetPosition();
                  onClose();
                }}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: '#8b4513',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                }}
                data-no-sync="true"
              >
                🗺️ Reset Position to Island Center
              </button>
              <p style={{ 
                fontSize: '12px', 
                color: '#888', 
                marginTop: '8px',
                marginBottom: 0,
              }}>
                Use this if you're stuck in the ocean or need to return to the starting area. Position: ({islandCenter.q}, {islandCenter.r})
              </p>
              
              {/* TEMPORARILY DISABLED FOR ALPHA TESTING - Uncomment to enable
              {onRegenerateMapClick && (
                <>
                  <button
                    onClick={() => {
                      if (window.confirm('This will generate a completely new map with different terrain. Your current position will be reset. Continue?')) {
                        onRegenerateMapClick();
                        onClose();
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '12px',
                      marginTop: '12px',
                      backgroundColor: '#d9534f',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: 'bold',
                    }}
                    data-no-sync="true"
                  >
                    🗺️ Regenerate Map
                  </button>
                  <p style={{ 
                    fontSize: '12px', 
                    color: '#888', 
                    marginTop: '8px',
                    marginBottom: 0,
                  }}>
                    Generates a completely new random map with different terrain layout. Your explored hexes will be cleared.
                  </p>
                </>
              )}
              */}
            </div>
          )}
        </div>

        <div style={{ 
          display: 'flex', 
          justifyContent: 'flex-end',
          marginTop: '16px',
          paddingTop: '16px',
          borderTop: '1px solid #3a3a3a',
          flexShrink: 0,
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              backgroundColor: '#4a4a4a',
              color: '#e0e0e0',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
            }}
            data-no-sync="true"
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// Helper function to get current travel time setting
export function getTravelTimeSetting(): number {
  const saved = localStorage.getItem(STORAGE_KEY);
  return saved ? parseInt(saved, 10) : 5000; // Default to 5 seconds per hex
}

// Helper function to get current monster probability setting
export function getMonsterProbabilitySetting(): number {
  const saved = localStorage.getItem(MONSTER_PROBABILITY_KEY);
  return saved ? parseFloat(saved) : 1/12; // Default to 1/12 (~8.33% encounter rate)
}

// Helper function to get current foraging time setting
export function getForagingTimeSetting(): number {
  const saved = localStorage.getItem(FORAGING_TIME_KEY);
  return saved ? parseInt(saved, 10) : 60 * 60 * 1000; // Default to 1 hour
}

// Helper function to get always nymph setting
export function getAlwaysNymphSetting(): boolean {
  const saved = localStorage.getItem(ALWAYS_NYMPH_KEY);
  return saved === 'true'; // Default to false
}

